import type { Api_Video, Api_Video_Poster } from '@core/api'
import { orderVideoFiles } from '@core/video/videoElementWrapper/utils/video'

import { pickPosterByWeight } from './pickPosterByWeight'
import type { CDNOption } from './roundRobinCDN'
import { roundRobinCDN } from './roundRobinCDN'

export { roundRobinCDN }

/**
 * Converts an aspect ratio string to a numeric array.
 * @param aspectRatio - The aspect ratio string to convert.
 * @returns An array of two numbers representing the width and height of the aspect ratio.
 */
export function aspectRatioToNumber(aspectRatio: string | number) {
  if (typeof aspectRatio === 'string') {
    const [width, height] = aspectRatio.split(':').map(Number)
    return width / height
  }
  return aspectRatio
}

/**
 * Returns a weighted pick among the jpeg posters from `video_posters`. Item typically
 * provided by user as a custom poster. Other auto-generated posters are not jpeg (gif and webp).
 */
export function getVideoPosterJpg(video?: Api_Video | null) {
  return pickPosterByWeight(video?.video_posters, (p) => p.format === 'jpg')
    ?.url
}

/**
 * Returns a weighted pick among the jpeg posters from `video_posters` with the closest aspect ratio.
 */
export function getVideoPosterByAspectRatio(
  video: Api_Video,
  aspectRatio: string,
): Api_Video_Poster | undefined {
  const aspectRatioNumber = aspectRatioToNumber(aspectRatio)
  return pickPosterByWeight(
    video?.video_posters,
    (poster) =>
      poster.format === 'jpg' &&
      (!aspectRatio ||
        (aspectRatioNumber >= 1
          ? aspectRatioToNumber(poster.aspect_ratio) >= 1
          : aspectRatioToNumber(poster.aspect_ratio) < 1)),
  )
}

/**
 * Returns the url of a weighted pick among the jpeg posters from `video_posters`
 * with the closest aspect ratio.
 */
export function getVideoPosterJpgByAspectRatio(
  video: Api_Video,
  aspectRatio: string,
) {
  return getVideoPosterByAspectRatio(video, aspectRatio)?.url || ''
}

/**
 * Returns the placeholder's blurhash for the first suitable video file from `video_files`.
 */
export function getVideoBlurhash(video?: Api_Video | null) {
  const orderedVideoFiles = video ? orderVideoFiles(video, []) : []
  return orderedVideoFiles[0]?.placeholder_blur_hash || ''
}

/**
 * Returns the placeholder url for the first suitable video file from `video_files`. Note
 * the placeholder is a first frame of the video.
 */
export function getVideoPlaceholder(
  video?: Api_Video | null,
  preferredCDNs?: CDNOption[],
) {
  const orderedVideoFiles = video ? orderVideoFiles(video, preferredCDNs) : []
  return orderedVideoFiles[0]?.placeholder_url || ''
}

/**
 * Returns first `jpg` poster from `video_posters` or `thumbnail_url`
 * if none found. This helper is legacy and does not take placeholder into
 * account.
 */
export function getVideoPoster(
  video?: Api_Video | null,
  preferredCDNs?: CDNOption[],
) {
  return roundRobinCDN(
    getVideoPosterJpg(video) || video?.thumbnail_url || '',
    preferredCDNs,
  )
}

/**
 * Gets a weighted pick among the video posters matching the given type (and optional format).
 * @param video a video with video_posters
 * @param type the type to search for
 * @param format the image format to search for
 */
export const getVideoPosterByType = (
  video: Api_Video,
  type: Api_Video_Poster['video_poster_type'],
  format?: Api_Video_Poster['format'],
): Api_Video_Poster | undefined =>
  pickPosterByWeight(
    video?.video_posters,
    (poster) =>
      poster.video_poster_type === type &&
      (format ? poster.format === format : true),
  )
